import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Table, Input, Switch, Tag, Space, Typography, message, Tooltip, Segmented } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined } from "@ant-design/icons";
import { useAppDispatch } from "../../utils/hooks/redux";
import {
    getClaimsForTestToggle,
    toggleTestClaim,
    TestClaimRow,
} from "../../redux/features/conversionTracking/conversionTrackingAsyncThunk";
import { LostStatuses } from "../../types/LostStatuses";

const { Text } = Typography;

interface Props {
    open: boolean;
    onClose: () => void;
    // Notify parent when a flag changes so it can re-fetch stats (so revenue/counts refresh).
    onChange?: () => void;
}

const STATUS_LABELS: Record<number, { text: string; color: string }> = {
    [LostStatuses.Deleted]: { text: "Deleted", color: "red" },
    [LostStatuses.Open]: { text: "Open", color: "blue" },
    [LostStatuses.Pending]: { text: "Pending", color: "gold" },
    [LostStatuses.Review]: { text: "Review", color: "purple" },
    [LostStatuses.InProcess]: { text: "In Process", color: "geekblue" },
    [LostStatuses.LFNotified]: { text: "LF Notified", color: "cyan" },
    [LostStatuses.Urgent]: { text: "Urgent", color: "red" },
    [LostStatuses.DraftIncomplete]: { text: "Draft Incomplete", color: "default" },
    [LostStatuses.DraftCompleted]: { text: "Draft Completed", color: "default" },
    [LostStatuses.NotPaid]: { text: "Not Paid", color: "orange" },
    [LostStatuses.Overdue]: { text: "Overdue", color: "volcano" },
};

const TestClaimsModal: React.FC<Props> = ({ open, onClose, onChange }) => {
    const dispatch = useAppDispatch();
    const [rows, setRows] = useState<TestClaimRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [view, setView] = useState<"all" | "test-only">("all");
    const [paymentStatus, setPaymentStatus] = useState<"all" | "paid" | "unpaid">("all");
    const [loading, setLoading] = useState(false);
    // Per-row toggle loading + optimistic state.
    const [updating, setUpdating] = useState<Record<string, boolean>>({});

    // Debounce search input so we don't refire on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchValue.trim()), 350);
        return () => clearTimeout(t);
    }, [searchValue]);

    const fetchClaims = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        try {
            const result = await dispatch(
                getClaimsForTestToggle({
                    offset: (page - 1) * pageSize,
                    limit: pageSize,
                    searchValue: debouncedSearch || undefined,
                    onlyTest: view === "test-only",
                    paymentStatus,
                })
            ).unwrap();
            setRows(result.data);
            setTotal(result.counts);
        } catch (err: any) {
            message.error(typeof err === "string" ? err : err?.message || "Failed to load claims");
        } finally {
            setLoading(false);
        }
    }, [dispatch, open, page, pageSize, debouncedSearch, view, paymentStatus]);

    useEffect(() => {
        fetchClaims();
    }, [fetchClaims]);

    // Reset paging when the search or any filter changes.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, view, paymentStatus]);

    const handleToggle = async (row: TestClaimRow, next: boolean) => {
        setUpdating((m) => ({ ...m, [row.id]: true }));
        // Optimistic update so the switch feels instant.
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isTestClaim: next } : r)));
        try {
            await dispatch(toggleTestClaim({ id: row.id, isTestClaim: next })).unwrap();
            message.success(next ? "Marked as test" : "Removed from test claims");
            onChange?.();
            // If filtering to test-only and we just unflagged a row, refresh so it drops out.
            if (view === "test-only" && !next) fetchClaims();
        } catch (err: any) {
            // Roll back optimistic state on failure.
            setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isTestClaim: !next } : r)));
            message.error(typeof err === "string" ? err : err?.message || "Update failed");
        } finally {
            setUpdating((m) => {
                const { [row.id]: _, ...rest } = m;
                return rest;
            });
        }
    };

    const columns = useMemo<ColumnsType<TestClaimRow>>(() => [
        {
            title: "Claim #",
            dataIndex: "trackingId",
            key: "trackingId",
            width: 110,
            render: (v: string) => <Text strong>{v || "—"}</Text>,
        },
        {
            title: "Customer",
            key: "customer",
            ellipsis: true,
            render: (_, r) => (
                <Tooltip title={r.customerEmail || ""}>
                    <div style={{ fontWeight: 500 }}>{r.customerName?.trim() || "—"}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{r.customerEmail || "—"}</div>
                </Tooltip>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 130,
            render: (v: number) => {
                const cfg = STATUS_LABELS[v] || { text: String(v), color: "default" };
                return <Tag color={cfg.color}>{cfg.text}</Tag>;
            },
        },
        {
            title: "Payment",
            key: "payment",
            width: 110,
            render: (_, r) =>
                r.hasSucceededPayment || r.isPaid ? (
                    <Tag color="green">Paid</Tag>
                ) : (
                    <Tag color="default">Not Paid</Tag>
                ),
        },
        {
            title: (
                <Tooltip title="Test claims are excluded from conversion-tracking counts, charts and revenue.">
                    <span>Test Claim</span>
                </Tooltip>
            ),
            key: "isTestClaim",
            width: 110,
            align: "center",
            render: (_, r) => (
                <Switch
                    checked={r.isTestClaim}
                    loading={!!updating[r.id]}
                    onChange={(checked) => handleToggle(r, checked)}
                />
            ),
        },
    ], [updating]);

    return (
        <Modal
            title="Manage Test Claims"
            open={open}
            onCancel={onClose}
            footer={null}
            width={900}
            destroyOnClose
        >
            <div style={{ marginBottom: 8, color: "#6b7280", fontSize: 13 }}>
                Toggle claims as <strong>Test</strong> to exclude them from the conversion-tracking page's
                counts, charts, and revenue calculations. Paid test claims are <strong>not</strong> counted
                as revenue. The claim remains visible in all other admin views.
            </div>
            <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", rowGap: 8 }}>
                <Input
                    allowClear
                    placeholder="Search by claim #, name or email"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    prefix={<SearchOutlined />}
                    style={{ width: 320 }}
                />
                <Space wrap>
                    <Segmented
                        value={paymentStatus}
                        onChange={(v) => setPaymentStatus(v as "all" | "paid" | "unpaid")}
                        options={[
                            { label: "All payments", value: "all" },
                            { label: "Paid", value: "paid" },
                            { label: "Unpaid", value: "unpaid" },
                        ]}
                    />
                    <Segmented
                        value={view}
                        onChange={(v) => setView(v as "all" | "test-only")}
                        options={[
                            { label: "All claims", value: "all" },
                            { label: "Test only", value: "test-only" },
                        ]}
                    />
                </Space>
            </Space>
            <Table
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={{
                    current: page,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (t) => `${t} claims`,
                    onChange: (p, s) => {
                        setPage(p);
                        if (s !== pageSize) setPageSize(s);
                    },
                }}
            />
        </Modal>
    );
};

export default TestClaimsModal;
